import { useSelection } from "@/contexts/SelectionContext";
import { useFragment } from "@/generated";
import {
  PublishGrammarFormSlidesDocument,
  SlideFieldsFragmentDoc,
  ToggleSlidesIsHiddenDocument,
  UpdateSlideDocument,
  type UpdateSlideInput,
} from "@/generated/graphql";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { ErrorCodes } from "@ai-teach/types";
import type { ApolloCache, Reference } from "@apollo/client";
import type { ModifierDetails } from "@apollo/client/cache";
import { useMutation } from "@apollo/client/react";

type ReadFieldFn = ModifierDetails["readField"];

/**
 * Recompute a lesson's cached `limitation` (all FOUR fields) from its grammar `form` slides,
 * EXCLUDING hidden / deleted / trashed — mirroring the server's `createSlidesLimitationLoader`
 * (which is scoped to `type:"grammar"` + `knowledgeArea:"form"`). Without this the Apollo cache
 * shows stale per-field limits after a slide edit (the client-side counterpart of C12 + W2).
 *
 * Note: the lesson's `accumulatedLimit` (cross-lesson) is NOT recomputed here — it refreshes on
 * the next fetch; recomputing the full project-tree accumulation client-side is out of scope.
 */
export function recomputeLessonGrammarLimit(
  cache: ApolloCache,
  readField: ReadFieldFn,
  lessonId: string,
  items: readonly (Reference | undefined)[]
) {
  const activeFormSlides = items.filter((ref): ref is Reference => {
    if (!ref) return false;
    return (
      readField<string>("type", ref) === "grammar" &&
      readField<string>("knowledgeArea", ref) === "form" &&
      !readField<boolean>("isHidden", ref) &&
      !readField<boolean>("isDeleted", ref) &&
      !readField<boolean>("isTrash", ref) &&
      // Only PUBLISHED grammar-form slides contribute to the lesson's limit (mirrors the server's
      // isCompleted gate). Use `!== false` so a slide whose cache entry is missing the field isn't
      // wrongly dropped. NOTE: authoritative enforcement lives server-side (resolveGrammarConversationGate
      // + the lesson.service form-slide match objects); this client recompute is best-effort UI only.
      readField<boolean>("isCompleted", ref) !== false
    );
  });

  // Each slide's limitation field is a single String; the lesson aggregates to [String!]!.
  const collect = (key: "grammarLimit" | "grammarFocus" | "wordLimit" | "wordFocus") =>
    activeFormSlides
      .map((ref) => readField<Record<string, string | undefined>>("limitation", ref)?.[key])
      .filter((v): v is string => Boolean(v));

  const limitationUpdate = {
    grammarLimit: collect("grammarLimit"),
    grammarFocus: collect("grammarFocus"),
    wordLimit: collect("wordLimit"),
    wordFocus: collect("wordFocus"),
  };

  cache.modify({
    id: `Lesson:${lessonId}`,
    fields: {
      limitation(existingData) {
        if (!existingData) return existingData;
        return { ...existingData, ...limitationUpdate };
      },
    },
  });
}

/**
 * Hook for updating a slide's content and properties.
 *
 * @example
 * const { updateSlideMutation, loading } = useUpdateSlide();
 * await updateSlideMutation({
 *   variables: {
 *     input: {
 *       slideId: "slide123",
 *       elements: [...updatedElements],
 *       name: "Updated Slide Name"
 *     }
 *   }
 * });
 */
export function useUpdateSlide() {
  const { toggleUnlinkModalOpen } = useSelection();

  const [updateSlideMutation, { loading }] = useMutation(UpdateSlideDocument, {
    update(cache, { data }) {
      if (!data?.updateSlide) return;

      const slides = data.updateSlide.map((maskedSlide) =>
        useFragment(SlideFieldsFragmentDoc, maskedSlide)
      );

      cache.batch({
        update(cache) {
          for (const slide of slides) {
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                slidesByLessonId(existingData, { storeFieldName, toReference, readField }) {
                  const key = `"lessonId":"${slide.lessonId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;

                  if (!existingData?.items) return existingData;

                  const newRef = toReference(slide);
                  const cacheId = cache.identify(slide);

                  const items = [
                    newRef,
                    ...existingData.items.filter(
                      (ref: Reference) => cache.identify(ref) !== cacheId
                    ),
                  ];

                  if (slide.type === "grammar") {
                    recomputeLessonGrammarLimit(cache, readField, slide.lessonId, items);
                  }

                  return {
                    ...existingData,
                    items,
                  };
                },
              },
            });
          }
        },
      });
    },
  });

  const updateSlide = async (input: UpdateSlideInput, screenshotFile?: File | null) => {
    try {
      const result = await updateSlideMutation({
        variables: { input, screenshotFile },
      });

      const maskedSlides = useFragment(SlideFieldsFragmentDoc, result.data?.updateSlide);
      const unmaskedSlide = maskedSlides?.find((slide) => slide.id === input.slideId);

      return unmaskedSlide;
    } catch (error) {
      if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
        toggleUnlinkModalOpen({ type: "Slide", itemId: input.slideId });
      }
      return;
    }
  };

  const [toggleSlidesIsHiddenMutation, { loading: toggleLoading }] = useMutation(
    ToggleSlidesIsHiddenDocument,
    {
      // Hiding/unhiding a grammar slide changes which slides contribute to the lesson's
      // grammar limit, so recompute the lesson's cached limitation from active slides.
      update(cache, { data }) {
        try {
          if (!data?.toggleSlidesIsHidden?.length) return;
          const slides = data.toggleSlidesIsHidden.map((s) =>
            useFragment(SlideFieldsFragmentDoc, s)
          );
          const lessonIds = new Set(
            slides.filter((s) => s.type === "grammar").map((s) => s.lessonId)
          );
          if (lessonIds.size === 0) return;

          cache.batch({
            update(cache) {
              for (const lessonId of lessonIds) {
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    slidesByLessonId(existingData, { storeFieldName, readField }) {
                      const key = `"lessonId":"${lessonId}"`;
                      const isTrashKey = '"isTrash":false';
                      if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                        return existingData;
                      if (!existingData?.items) return existingData;

                      recomputeLessonGrammarLimit(cache, readField, lessonId, existingData.items);
                      return existingData;
                    },
                  },
                });
              }
            },
          });
        } catch (error) {
          console.error(
            "[useUpdateSlide] Failed to recompute lesson limitation on hide toggle",
            error
          );
        }
      },
    }
  );

  // Throws on failure so callers (CardActionBar / HideModal) can surface the error their own way.
  const toggleSlidesIsHidden = async (slideIds: string[], isHidden: boolean) => {
    const result = await toggleSlidesIsHiddenMutation({
      variables: {
        slideIds,
        isHidden,
      },
    });

    const maskedSlides = result.data?.toggleSlidesIsHidden;
    return maskedSlides
      ? maskedSlides.map((m) => useFragment(SlideFieldsFragmentDoc, m))
      : undefined;
  };

  const [publishGrammarFormSlidesMutation, { loading: publishLoading }] = useMutation(
    PublishGrammarFormSlidesDocument,
    {
      // Publishing grants isCompleted (and clears isHidden on the originals), which changes which
      // slides contribute to the lesson's grammar limit. We MUST NOT recompute the aggregate from
      // the cached `slidesByLessonId` here: that list can be a filtered/paginated subset (the admin
      // "Filter by" dropdown + pagination), so recomputing would overwrite the lesson's aggregate
      // limitation with only a subset. The server is authoritative — evict the affected lessons'
      // cached `limitation` and refetch the query that owns it so Apollo re-queries the real value.
      update(cache, { data }) {
        try {
          if (!data?.publishGrammarFormSlides?.length) return;
          const slides = data.publishGrammarFormSlides.map((s) =>
            useFragment(SlideFieldsFragmentDoc, s)
          );
          const lessonIds = new Set(slides.map((s) => s.lessonId));
          if (lessonIds.size === 0) return;

          for (const lessonId of lessonIds) {
            cache.evict({ id: `Lesson:${lessonId}`, fieldName: "limitation" });
          }
          cache.gc();
        } catch (error) {
          console.error(
            "[useUpdateSlide] Failed to evict lesson limitation on grammar form publish",
            error
          );
        }
      },
      // `GetLessonWithLimitation` is the only query carrying `Lesson.limitation`. Refetching it by
      // operation name re-runs whatever instances are active with their current variables, so the
      // aggregate limit is restored from the server instead of a possibly-partial client recompute.
      refetchQueries: ["GetLessonWithLimitation"],
    }
  );

  // Throws on failure (e.g. GRAMMAR_FORM_NOT_PUBLISHABLE) so callers can surface the server's
  // validation issues their own way instead of a generic error.
  const publishGrammarFormSlides = async (slideIds: string[]) => {
    const result = await publishGrammarFormSlidesMutation({
      variables: { slideIds },
    });

    const maskedSlides = result.data?.publishGrammarFormSlides;
    return maskedSlides ? maskedSlides.map((m) => useFragment(SlideFieldsFragmentDoc, m)) : [];
  };

  return {
    updateSlide,
    toggleSlidesIsHidden,
    publishGrammarFormSlides,
    loading,
    toggleLoading,
    publishLoading,
  };
}
